import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null = unknown, false = guest
  const [loading, setLoading] = useState(true);

  // Reflect the current user's role onto <html> so global CSS can gate
  // admin-only UI (e.g. delete buttons) for non-admin users.
  useEffect(() => {
    const root = document.documentElement;
    if (user && user.role) root.dataset.userRole = user.role;
    else delete root.dataset.userRole;
  }, [user]);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("ttl_token");
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ttl_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("ttl_token");
    setUser(false);
  };

  // Force a re-fetch of /auth/me — used e.g. after a first-login password change.
  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch { /* ignore */ }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
